import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/axios";
import { useI18n } from "../../i18n/I18nContext";

export default function OrderDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const isAdminView = location.pathname.startsWith("/admin");
  const isEmailAccessView = location.pathname.startsWith("/order-access");
  const emailToken = searchParams.get("token");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [noticeMsg, setNoticeMsg] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedStatus, setSelectedStatus] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState("");

  const itemsPerPage = 3;

  const translations = useMemo(
    () => ({
      pending: t("Pendente"),
      pending_payment: t("Aguarda Pagamento"),
      paid: t("Pago"),
      processing: t("Em Processamento"),
      shipped: t("Enviado"),
      delivered: t("Entregue"),
      cancelled: t("Cancelada"),
      refunded: t("Reembolsada"),
    }),
    [t]
  );

  const statusTransitions = {
    pending: ["pending", "pending_payment", "paid", "cancelled"],
    pending_payment: ["pending_payment", "paid", "cancelled"],
    paid: ["paid", "processing", "cancelled", "refunded"],
    processing: ["processing", "shipped", "cancelled", "refunded"],
    shipped: ["shipped", "delivered", "refunded"],
    delivered: ["delivered", "refunded"],
    cancelled: ["cancelled", "refunded"],
    refunded: ["refunded"],
  };

  const getOrderEndpoint = () => {
    if (isEmailAccessView) {
      return `/api/v1/orders/email-access/${id}?token=${encodeURIComponent(emailToken || "")}`;
    }
    return isAdminView ? `/api/admin/orders/${id}` : `/api/v1/orders/${id}`;
  };

  const translateStatus = (status) => translations[status] || status || "-";
  const formatAmount = (value) => Number(value ?? 0).toFixed(2);

  // ── helpers de campos com fallback formato antigo → novo ──────────────────

  const getItemName = (item) => item.name ?? item.product_name ?? "-";
  const getItemVariantLabel = (item) => item.variant_label ?? item.variant?.label ?? "-";

  const getShipping = () =>
    order?.shipping_cost ??
    order?.amounts?.shipping_amount ??
    order?.shipping_amount ??
    "0.00";

  const getDiscount = () =>
    order?.discount_amount ??
    order?.amounts?.discount_amount ??
    "0.00";

  const getTotal = () =>
    order?.total_estimated ??
    order?.amounts?.total_amount ??
    order?.total_amount ??
    "0.00";

  const getSubtotalWithoutIva = () =>
    order?.subtotal_without_iva ??
    order?.amounts?.subtotal_amount ??
    order?.subtotal_amount ??
    "0.00";

  const getSubtotalWithIva = () =>
    order?.subtotal ??
    order?.amounts?.subtotal_with_iva ??
    "0.00";

  const getIvaAmount = () =>
    order?.iva_amount ??
    order?.amounts?.iva_amount ??
    Number(getSubtotalWithIva()) - Number(getSubtotalWithoutIva()); 

    
  const getIvaRate = () => order?.iva_rate ?? null;
  const getTotalWeightKg = () => order?.total_weight_kg ?? null;
  const getPlacedAt = () => order?.placed_at ?? order?.created_at ?? "-";

  // user — presente no admin (whenLoaded), pode ser null noutras rotas
  const getCustomerName = () => order?.user?.name ?? null;
  const getCustomerEmail = () => order?.user?.email ?? null;

  // ─────────────────────────────────────────────────────────────────────────

  const loadOrder = async () => {
    setLoading(true);
    setErrorMsg("");
    setNoticeMsg("");
    try {
      const res = await api.get(getOrderEndpoint());
      const orderData = res.data?.data || res.data?.order || res.data;
      setOrder(orderData);
      setCurrentPage(1);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro a carregar a encomenda."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) { setLoading(false); setErrorMsg(t("Encomenda inválida.")); return; }
    if (isEmailAccessView && !emailToken) { setLoading(false); setErrorMsg(t("Link inválido.")); return; }
    loadOrder();
  }, [id, isAdminView, isEmailAccessView, emailToken]);

  useEffect(() => {
    if (order?.status) setSelectedStatus(String(order.status).trim());
  }, [order?.status]);

  const normalizedOrderStatus = String(order?.status || "").trim();
  const normalizedSelectedStatus = String(selectedStatus || "").trim();
  const currentStatus = normalizedSelectedStatus || normalizedOrderStatus;

  const availableStatuses =
    statusTransitions[normalizedOrderStatus] ||
    (normalizedOrderStatus ? [normalizedOrderStatus] : []);

  const openConfirmModal = () => {
    if (!currentStatus || currentStatus === normalizedOrderStatus) return;
    setPendingStatus(currentStatus);
    setIsConfirmModalOpen(true);
  };

  const closeConfirmModal = () => {
    if (savingStatus) return;
    setIsConfirmModalOpen(false);
    setPendingStatus("");
  };

  const handleUpdateStatus = async () => {
    if (!isAdminView || !order || !pendingStatus || pendingStatus === normalizedOrderStatus) return;
    try {
      setSavingStatus(true);
      setErrorMsg("");
      setNoticeMsg("");
      const res = await api.patch(`/api/admin/orders/${id}/status`, { status: pendingStatus });
      const updatedOrder = res.data?.data || res.data || null;
      setOrder((prev) => ({
        ...prev,
        status: updatedOrder?.status || pendingStatus,
      }));

      setSelectedStatus(String(updatedOrder?.status || pendingStatus).trim());
      setNoticeMsg(res.data?.message || t("Estado atualizado com sucesso."));
      setIsConfirmModalOpen(false);
      setPendingStatus("");
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao atualizar o estado da encomenda."));
    } finally {
      setSavingStatus(false);
    }
  };

  const orderSteps = [
    { key: "pending_payment", label: t("Aguarda Pagamento") },
    { key: "paid", label: t("Pago") },
    { key: "processing", label: t("Processamento") },
    { key: "shipped", label: t("Enviado") },
    { key: "delivered", label: t("Entregue") },
  ];

  const getCurrentStepIndex = (status) => {
    if (status === "pending") return 0;
    const index = orderSteps.findIndex((s) => s.key === status);
    return index === -1 ? 0 : index;
  };

  const totalPages = order?.items ? Math.ceil(order.items.length / itemsPerPage) : 1;
  const paginatedItems = order?.items
    ? order.items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : [];

  const hasWeightData = order?.items?.some((i) => i.weight_kg !== undefined);

  const getStatusBadgeClass = (status) => {
    if (status === "delivered") return "bg-emerald-100 text-emerald-700";
    if (status === "cancelled" || status === "refunded") return "bg-red-100 text-red-700";
    if (status === "shipped") return "bg-blue-100 text-blue-700";
    if (status === "processing") return "bg-amber-100 text-amber-700";
    if (status === "paid") return "bg-violet-100 text-violet-700";
    return "bg-gray-100 text-gray-700";
  };

  if (loading) return <p className="py-16 text-center text-gray-500">{t("A carregar encomenda...")}</p>;
  if (!order) return <p className="py-16 text-center text-gray-500">{errorMsg || t("Encomenda não encontrada")}</p>;

  const subtotalWithoutIva = getSubtotalWithoutIva();
  const ivaRate = getIvaRate();
  const totalWeightKg = getTotalWeightKg();
  const customerName = getCustomerName();
  const customerEmail = getCustomerEmail();

  return (
    <>
      <div className="min-h-screen bg-gray-50 px-6 py-10">
        <div className="mx-auto max-w-6xl">

          <h1 className="mb-6 text-2xl font-bold text-gray-900">
            {t("Encomenda")} #{order.order_number}
          </h1>

          {noticeMsg && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {noticeMsg}
            </div>
          )}
          {errorMsg && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMsg}
            </div>
          )}

          {/* ── ADMIN PANEL ──────────────────────────────────────────────── */}
          {isAdminView && (
            <div className="mb-6 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
              <div className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-6 py-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">{t("Gestão da Encomenda")}</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("Consulta os dados do cliente e altera o estado da encomenda.")}
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadgeClass(normalizedOrderStatus)}`}>
                    {translateStatus(normalizedOrderStatus)}
                  </span>
                </div>
              </div>

              <div className="grid gap-6 p-6 lg:grid-cols-[1.2fr_1fr]">
                <div className="grid gap-4 sm:grid-cols-2">

                  {/* Cliente — vem sempre no admin via with('user') */}
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 sm:col-span-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {t("Cliente")}
                    </p>
                    {customerName || customerEmail ? (
                      <div className="mt-2 flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 text-sm font-bold text-gray-600">
                          {(customerName ?? customerEmail ?? "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{customerName ?? "-"}</p>
                          <p className="text-sm text-gray-500">{customerEmail ?? "-"}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-2 text-sm italic text-gray-400">{t("Sem dados de cliente disponíveis.")}</p>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Estado Atual")}</p>
                    <p className="mt-2 text-sm font-semibold text-gray-900">{translateStatus(normalizedOrderStatus)}</p>
                    <p className="mt-1 text-sm text-gray-600">{t("Podes corrigir o estado manualmente se necessário.")}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Nº Encomenda")}</p>
                    <p className="mt-2 text-sm font-semibold text-gray-900">#{order.order_number || "-"}</p>
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Total c/ IVA")}</p>
                    <p className="mt-2 text-sm font-semibold text-gray-900">€ {formatAmount(getTotal())}</p>
                    {subtotalWithoutIva !== null && (
                      <p className="mt-1 text-xs text-gray-400">{t("s/ IVA:")} € {formatAmount(subtotalWithoutIva)}</p>
                    )}
                  </div>

                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("Peso Total")}</p>
                    <p className="mt-2 text-sm font-semibold text-gray-900">
                      {totalWeightKg !== null ? `${totalWeightKg} kg` : "-"}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      {t("Moeda:")} {order.currency ?? "EUR"}
                    </p>
                  </div>

                </div>

                {/* Selector de estado */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">{t("Atualizar estado")}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {t("Seleciona o novo estado e confirma a alteração.")}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">{t("Novo estado")}</label>
                      <div className="flex flex-wrap gap-2 rounded-xl bg-gray-100 p-2">
                        {availableStatuses.map((status) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() => setSelectedStatus(status)}
                            className={`rounded-lg px-3 py-2 text-sm font-medium transition ${
                              currentStatus === status
                                ? "bg-white text-gray-900 shadow"
                                : "text-gray-500 hover:text-gray-700"
                            }`}
                          >
                            {translateStatus(status)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-3 text-sm text-gray-600">
                      {t("Estado selecionado:")}{" "}
                      <span className="font-semibold text-gray-900">{translateStatus(currentStatus)}</span>
                    </div>
                    <button
                      onClick={openConfirmModal}
                      disabled={savingStatus || !currentStatus || currentStatus === normalizedOrderStatus}
                      className="w-full rounded-xl bg-gray-900 px-4 py-3 text-sm font-semibold text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t("Confirmar alteração")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── RESUMO ───────────────────────────────────────────────────── */}
          <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <h2 className="text-sm font-semibold text-gray-900">{t("Resumo da Encomenda")}</h2>
            </div>

            <div className="space-y-8 p-6">
              {/* Progress bar */}
              <div className="relative">
                <div className="absolute left-0 top-5 h-0.5 w-full bg-gray-200" />
                <div
                  className={`absolute left-0 top-5 h-0.5 transition-all duration-500 ${
                    normalizedOrderStatus === "cancelled" || normalizedOrderStatus === "refunded"
                      ? "bg-red-500"
                      : "bg-emerald-500"
                  }`}
                  style={{
                    width: `${(getCurrentStepIndex(normalizedOrderStatus) / (orderSteps.length - 1)) * 100}%`,
                  }}
                />
                <div className="relative flex justify-between gap-2">
                  {orderSteps.map((step, index) => {
                    const isCancelled =
                      normalizedOrderStatus === "cancelled" || normalizedOrderStatus === "refunded";
                    const currentIndex = getCurrentStepIndex(normalizedOrderStatus);
                    const isActive = !isCancelled && index <= currentIndex;
                    return (
                      <div key={step.key} className="flex flex-col items-center">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-all ${
                            isCancelled
                              ? "border-red-500 bg-red-500 text-white"
                              : isActive
                              ? "border-emerald-600 bg-emerald-600 text-white"
                              : "border-gray-300 bg-white text-gray-400"
                          }`}
                        >
                          {isCancelled ? "✕" : isActive ? "✓" : index + 1}
                        </div>
                        <span className={`mt-2 text-center text-xs font-medium ${isActive ? "text-emerald-600" : "text-gray-500"}`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid gap-8 md:grid-cols-2">
                {/* Valores */}
                <div className="space-y-2 text-gray-800">
                  {subtotalWithoutIva !== null && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{t("Subtotal s/ IVA")}</span>
                      <span>€ {formatAmount(subtotalWithoutIva)}</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span>{t("Subtotal c/ IVA")}</span>
                    <span>{formatAmount(getSubtotalWithIva())} €</span>
                  </div>

                  {ivaRate !== null && subtotalWithoutIva !== null && (
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>{t("IVA")} ({Math.round(ivaRate * 100)}%)</span>
                      <span>{formatAmount(getIvaAmount())} €</span>
                    </div>
                  )}

                  <div className="flex justify-between">
                    <span>{t("Envio")}</span>
                    <span>€ {formatAmount(getShipping())}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>{t("Desconto")}</span>
                    <span>- € {formatAmount(getDiscount())}</span>
                  </div>

                  <div className="flex justify-between border-t pt-2 text-lg font-bold">
                    <span>{t("Total")}</span>
                    <span>€ {formatAmount(getTotal())}</span>
                  </div>

                  {totalWeightKg !== null && (
                    <div className="flex justify-between border-t pt-2 text-sm text-gray-500">
                      <span>{t("Peso total")}</span>
                      <span>{totalWeightKg} kg</span>
                    </div>
                  )}
                </div>

                {/* Metadados */}
                <div className="space-y-2 text-gray-700">
                  <p>
                    <span className="font-medium">{t("Estado atual:")}</span>{" "}
                    <span className="capitalize">{translateStatus(normalizedOrderStatus)}</span>
                  </p>
                  <p>
                    <span className="font-medium">{t("Moeda:")}</span> {order.currency ?? "EUR"}
                  </p>
                  <p>
                    <span className="font-medium">{t("Feita a:")}</span> {getPlacedAt()}
                  </p>
                  <p>
                    <span className="font-medium">{t("Paga a:")}</span> {order.paid_at || "-"}
                  </p>

                  {/* Cliente — só mostra se vier no payload e não for email-access */}
                  {(customerName || customerEmail) && !isEmailAccessView && (
                    <>
                      <p>
                        <span className="font-medium">{t("Cliente:")}</span> {customerName ?? "-"}
                      </p>
                      <p>
                        <span className="font-medium">{t("Email:")}</span> {customerEmail ?? "-"}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ── ENDEREÇOS ────────────────────────────────────────────────── */}
          <div className="mb-6 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
              {t("Endereços")}
            </div>
            <div className="grid gap-6 p-4 text-gray-800 md:grid-cols-2">
              {order.shipping_address || order.billing_address ? (
                <>
                  {order.shipping_address && (
                    <div>
                      <h3 className="mb-2 font-semibold text-gray-900">{t("Morada de Envio")}</h3>
                      <p>{order.shipping_address.recipient_name}</p>
                      <p>{order.shipping_address.line1}</p>
                      {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
                      <p>{order.shipping_address.postal_code} {order.shipping_address.city}</p>
                      <p>{order.shipping_address.country_code}</p>
                      <p className="text-sm text-gray-600">{t("Tel:")} {order.shipping_address.phone}</p>
                    </div>
                  )}
                  {order.billing_address && (
                    <div>
                      <h3 className="mb-2 font-semibold text-gray-900">{t("Morada de Faturação")}</h3>
                      <p>{order.billing_address.recipient_name}</p>
                      <p>{order.billing_address.line1}</p>
                      {order.billing_address.line2 && <p>{order.billing_address.line2}</p>}
                      <p>{order.billing_address.postal_code} {order.billing_address.city}</p>
                      <p>{order.billing_address.country_code}</p>
                      <p className="text-sm text-gray-600">{t("Tel:")} {order.billing_address.phone}</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="col-span-2 py-6 text-center text-gray-500">
                  {t("Sem endereços na encomenda.")}
                </div>
              )}
            </div>
          </div>

          {/* ── ITENS ────────────────────────────────────────────────────── */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
              {t("Itens da Encomenda")}
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <tr>
                    <th className="p-3">{t("Produto")}</th>
                    <th className="p-3">{t("Variante")}</th>
                    <th className="p-3">{t("Qtd.")}</th>
                    <th className="p-3">{t("Preço Unit.")}</th>
                    <th className="p-3">{t("Subtotal")}</th>
                    {hasWeightData && <th className="p-3">{t("Peso")}</th>}
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {paginatedItems.length > 0 ? (
                    paginatedItems.map((item) => (
                      <tr key={item.id} className="border-t hover:bg-gray-50/60">

                        {/* Produto: thumbnail + nome + tipo de variante */}
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            {item.image_url ? (
                              <img
                                src={item.image_url}
                                alt={getItemName(item)}
                                className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 shrink-0 rounded-lg border border-gray-200 bg-gray-100" />
                            )}
                            <div>
                              <p className="font-medium leading-tight text-gray-900">
                                {getItemName(item)}
                              </p>
                              {item.variant_type && (
                                <p className="text-xs text-gray-400">{item.variant_type}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="p-3 text-gray-600">{getItemVariantLabel(item)}</td>
                        <td className="p-3">{item.quantity}</td>

                        {/* Preço unitário com/sem IVA */}
                        <td className="p-3">
                          <p>€ {formatAmount(item.unit_price)}</p>
                          {item.unit_price_without_iva !== undefined && (
                            <p className="text-xs text-gray-400">
                              {t("s/ IVA:")} € {formatAmount(item.unit_price_without_iva)}
                            </p>
                          )}
                        </td>

                        {/* Subtotal com/sem IVA */}
                        <td className="p-3">
                          <p>€ {formatAmount(item.line_total)}</p>
                          {item.line_total_without_iva !== undefined && (
                            <p className="text-xs text-gray-400">
                              {t("s/ IVA:")} € {formatAmount(item.line_total_without_iva)}
                            </p>
                          )}
                        </td>

                        {/* Peso — coluna só existe se algum item tiver weight_kg */}
                        {hasWeightData && (
                          <td className="p-3 text-gray-500">
                            {item.line_weight_kg !== undefined ? `${item.line_weight_kg} kg` : "-"}
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="p-10 text-center text-gray-400" colSpan={hasWeightData ? 6 : 5}>
                        {t("Sem itens na encomenda.")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {order?.items && order.items.length > itemsPerPage && (
                <div className="flex items-center justify-end space-x-2 px-4 py-2">
                  <button
                    className="rounded bg-gray-200 px-3 py-1 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    {t("Anterior")}
                  </button>
                  <span className="text-sm">{t(`Página ${currentPage} de ${totalPages}`)}</span>
                  <button
                    className="rounded bg-gray-200 px-3 py-1 disabled:opacity-50"
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    {t("Próximo")}
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── MODAL CONFIRMAÇÃO ────────────────────────────────────────────── */}
      {isAdminView && isConfirmModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">{t("Confirmar alteração")}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t("Confirma que queres alterar o estado desta encomenda?")}
              </p>
            </div>
            <div className="space-y-4 px-6 py-5">
              <div className="rounded-xl bg-gray-50 p-4 text-sm text-gray-700">
                <p><span className="font-medium">{t("Encomenda:")}</span> #{order.order_number || "-"}</p>
                {(customerName || customerEmail) && (
                  <p className="mt-2">
                    <span className="font-medium">{t("Cliente:")}</span> {customerName ?? customerEmail}
                  </p>
                )}
                <p className="mt-2">
                  <span className="font-medium">{t("Estado atual:")}</span> {translateStatus(normalizedOrderStatus)}
                </p>
                <p className="mt-2">
                  <span className="font-medium">{t("Novo estado:")}</span> {translateStatus(pendingStatus)}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                {t("Esta ação vai atualizar imediatamente o estado da encomenda no sistema.")}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={closeConfirmModal}
                disabled={savingStatus}
                className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t("Cancelar")}
              </button>
              <button
                type="button"
                onClick={handleUpdateStatus}
                disabled={savingStatus}
                className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {savingStatus ? t("A guardar...") : t("Sim, alterar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}